import React, { useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, GraduationCap, Users, UploadCloud, Mail,
  Settings, ShieldCheck, ScrollText, LogOut, Bell, CalendarDays, Menu, UserSquare,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/corsi", label: "Corsi", icon: GraduationCap },
  { to: "/calendario", label: "Calendario", icon: CalendarDays },
  { to: "/importazioni", label: "Importazioni", icon: UploadCloud },
  { to: "/email", label: "Modello E-mail", icon: Mail },
  { to: "/segnalazioni", label: "Segnalazioni", icon: Bell },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const nav = [...NAV];
  if (user?.role === "super_admin")
    nav.splice(6, 0, { to: "/docenti", label: "Docenti", icon: UserSquare }, { to: "/utenti", label: "Utenti", icon: ShieldCheck });

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 shrink-0 hidden lg:flex flex-col bg-[#0B192C] text-white sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src="/logo_csa.jpg" alt="Centro Studi Athena" className="h-10 w-10 rounded-xl bg-white object-contain p-1" data-testid="sidebar-logo" />
            <div>
              <div className="font-display font-bold leading-tight">Athena</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider">Registro Presenze</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `athena-sidebar-link ${isActive ? "active" : ""}`}>
              <n.icon size={18} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-[11px] text-slate-400 mb-3 capitalize">{user?.role?.replace("_", " ")}</div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors">
            <LogOut size={16} /> Esci
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent side="left" className="w-72 bg-[#0B192C] text-white border-white/10 p-0 flex flex-col" data-testid="mobile-menu">
            <SheetTitle className="px-6 py-5 border-b border-white/10 flex items-center gap-3 text-white">
              <img src="/logo_csa.jpg" alt="Centro Studi Athena" className="h-9 w-9 rounded-lg bg-white object-contain p-0.5" />
              <span className="font-display font-bold">Athena Presenze</span>
            </SheetTitle>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) => `athena-sidebar-link ${isActive ? "active" : ""}`}>
                  <n.icon size={18} />
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="px-4 py-4 border-t border-white/10">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <button onClick={() => { logout(); navigate("/login"); }} data-testid="mobile-logout-btn"
                className="mt-2 flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors">
                <LogOut size={16} /> Esci
              </button>
            </div>
          </SheetContent>
        </Sheet>
        <header className="lg:hidden sticky top-0 z-20 bg-[#0B192C] text-white px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMenuOpen(true)} aria-label="Apri menu" data-testid="mobile-menu-btn"
            className="h-9 w-9 grid place-items-center rounded-lg hover:bg-white/10 transition-colors mr-2">
            <Menu size={20} />
          </button>
          <span className="flex-1 flex items-center gap-2 font-display font-bold"><img src="/logo_csa.jpg" alt="Centro Studi Athena" className="h-8 w-8 rounded-lg bg-white object-contain p-0.5" data-testid="mobile-logo" />Athena Presenze</span>
          <button onClick={() => { logout(); navigate("/login"); }}><LogOut size={18} /></button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto animate-fade-up">{children}</main>
      </div>
    </div>
  );
}
